import { DateTime } from 'luxon';
import Modal from 'smg/modal';
import { Start, Stop } from 'smg/buttons';
import { Child, NewQuoteRequest, Extra } from 'smg/customer-api/quoterequests';
import { HandleRequestError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import { Session } from 'smg/customer-api/session';
import { FullName } from 'smg/users';
import { Phone } from 'smg/phone';
import { Uploader } from '../components/uploader';
import { Relref } from '../hugo';
import * as html from 'smg/html';
import * as smgPromo from 'smg/customer-api/promos';

declare interface myWindow extends Window {
    dataLayer: Record<string, unknown>[];
}
declare const window: myWindow;

if (document.body.id === 'trading-pin-get-started') {
    main();
}

function main() {
    const $form = document.querySelector('#trading-pin-quote-form') as HTMLFormElement;
    const $sportSelect = $form.querySelector('.step.sport') as HTMLElement;
    const $templateSelect = $form.querySelector('.step.template') as HTMLElement;
    const $sizeSelect = $form.querySelector('.step.size') as HTMLElement;
    const $platingSelect = $form.querySelector('.step.plating') as HTMLElement;
    const $upgradesSelect = $form.querySelector('.step.upgrades') as HTMLElement;

    $sportSelect.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach(radio =>
        radio.addEventListener('input', () => {
            if (radio.value === 'Baseball') {
                $form.querySelectorAll<HTMLElement>('.baseball-img').forEach(img => img.classList.remove('d-none'));
                $form.querySelectorAll<HTMLElement>('.softball-img').forEach(img => img.classList.add('d-none'));
            } else {
                $form.querySelectorAll<HTMLElement>('.softball-img').forEach(img => img.classList.remove('d-none'));
                $form.querySelectorAll<HTMLElement>('.baseball-img').forEach(img => img.classList.add('d-none'));
            }

            $templateSelect.scrollIntoView({
                behavior: 'smooth',
            });
        }));

    $templateSelect.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach(radio => {
        radio.addEventListener('input', () => {
            $sizeSelect.scrollIntoView({
                behavior: 'smooth',
            });
        });
    });

    $sizeSelect.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach(radio => {
        radio.addEventListener('input', () => {
            $platingSelect.scrollIntoView({
                behavior: 'smooth',
            });
        });
    });

    $platingSelect.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach(radio => {
        radio.addEventListener('input', () => {
            $upgradesSelect.scrollIntoView({
                behavior: 'smooth',
            });
        });
    });

    const $contactFieldset = $form.querySelector('#contact-form') as HTMLFieldSetElement;

    const $quantity = $form.querySelector('#quantity') as HTMLInputElement;
    const $budget = $form.querySelector('#budget') as HTMLSelectElement;
    const $projectName = $form.querySelector('#project-name') as HTMLInputElement;

    const $teamName = $form.querySelector('#team-name') as HTMLInputElement;
    const $teamColors = $form.querySelector('#team-colors') as HTMLInputElement;
    const $teamHomeState = $form.querySelector('#team-home-state') as HTMLSelectElement;

    const $notes = $form.querySelector('#comments') as HTMLTextAreaElement;

    const $firstName = $form.querySelector('#first-name') as HTMLInputElement;
    const $lastName = $form.querySelector('#last-name') as HTMLInputElement;
    const $email = $form.querySelector('#email') as HTMLInputElement;
    const $phone = $form.querySelector('#phone') as HTMLInputElement;

    const $promo = $form.querySelector('#promo') as HTMLInputElement;
    const $promoApply = $form.querySelector('#promo-apply') as HTMLButtonElement;
    const $promoAccepted = $form.querySelector('#promo-accepted') as HTMLDivElement;
    const $date = $form.querySelector('#date-needed') as HTMLInputElement;

    const $submit = $form.querySelector('[type="submit"]') as HTMLButtonElement;

    const u = new Uploader(document.querySelector('.uploader-dropzone') as HTMLLabelElement, {
        confirmBtn: $submit || undefined,
    });

    const p = new Phone($phone);

    const promoInput = new smgPromo.Input($promo, {
        success: p => {
            $promoAccepted.classList.remove('d-none');
            $promoAccepted.innerHTML = `<b>${html.Escape(p.code)}:</b> ${html.Escape(p.description)}`;
        },
        error: () => {
            $promoAccepted.classList.add('d-none');
            $promoAccepted.innerHTML = '';
        },
    });

    $promoApply.addEventListener('click', async () => {
        promoInput.Dirty = true;
        Start($promoApply);
        await promoInput.Check().then(() => {
            Stop($promoApply);
        }).catch(() => {
            Stop($promoApply);
        });
    });

    // make sure the promo is checked if it's loaded from the url param
    // and run the success/error callbacks
    setTimeout(async () => {
        promoInput.Dirty = true;
        await promoInput.Check().then(() => {
            if (!promoInput.$input.checkValidity()) {
                promoInput.$input.value = '';
                promoInput.$input.setCustomValidity('');
                void promoInput.Check();
            }
        });
    });

    $form.noValidate = true;
    $form.addEventListener('submit', async e => {
        e.preventDefault();

        const startLoading = () => {
            $contactFieldset.disabled = true;
            Start($submit);
        };
        const stopLoading = () => {
            $contactFieldset.disabled = false;
            Stop($submit);
        };

        startLoading();

        await promoInput.Check();

        $date.setCustomValidity('');
        const date = DateTime.fromISO($date.value);
        // Luxon DateTimes always include the time component, so this fromISO will be set to midnight of that day.
        // DateTime.now() is set to the current time, but we only care about the date component, so we set it to midnight.
        // If we want to allow users to set the same calendar day, just change the comparator to < instead of <=.
        if (date <= DateTime.now().startOf('day')) {
            $date.setCustomValidity('The date must be in the future.');
        }

        stopLoading();
        if (!ReportValidity($form)) {
            if ($sportSelect.classList.contains('is-invalid')) {
                $sportSelect.scrollIntoView({
                    behavior: 'smooth',
                });
            }
            return false;
        }
        startLoading();

        const $sportSelected = document.querySelector<HTMLInputElement>('[name="sport"]:checked') as HTMLInputElement;
        const $templateSelected = document.querySelector<HTMLInputElement>('[name="template"]:checked');
        const $sizeSelected = document.querySelector<HTMLInputElement>('[name="size"]:checked');
        const $platingSelected = document.querySelector<HTMLInputElement>('[name="plating"]:checked');
        const $upgradesSelected = document.querySelectorAll<HTMLInputElement>('[name="upgrades"]:checked');

        const extras: Extra[] = [];

        extras.push({
            name: 'Sport',
            value: $sportSelected.value,
        });

        if ($templateSelected?.value) {
            extras.push({
                name: 'Template',
                value: $templateSelected.value,
            });
        }

        if ($teamName?.value) {
            extras.push({
                name: 'Team',
                value: $teamName.value,
            });
        }

        if ($teamColors) {
            if ($teamColors.value) {
                extras.push({
                    name: 'Colors',
                    value: $teamColors.value,
                });
            }
        }

        if ($teamHomeState) {
            if ($teamHomeState.value) {
                extras.push({
                    name: 'Home State',
                    value: $teamHomeState.value,
                });
            }
        }

        if ($budget.value) {
            extras.push({
                name: 'Budget',
                value: $budget.value,
            });
        }

        const children: Child[] = [];

        if ($platingSelected?.value) {
            children.push({
                child: 'Plating',
                option: $platingSelected.value,
            });
        }

        $upgradesSelected.forEach(upgrade => {
            const upgradeEl = upgrade as HTMLInputElement;
            children.push({
                child: upgradeEl.value,
            });
        });

        void NewQuoteRequest({
            company: $teamName?.value || '',
            email: $email?.value || '',
            name: FullName({ firstName: $firstName?.value, lastName: $lastName?.value }),
            phone: p,

            dateNeeded: date.toISODate() ?? undefined,
            lineItem: 'Lapel Pins',
            type: 'Trading Pins',
            size: $sizeSelected?.value,
            extras,
            children,

            uploader: u,
            project: $projectName.value || `${new Date().getFullYear()} ${$teamName ? $teamName.value : ''}`,
            notes: $notes?.value || '',
            promo: promoInput.Dirty ? undefined : $promo.value,
            quantity: parseInt($quantity?.value, 10) || 0,
        }).then(async resp => {
            const sess = await Session().catch(() => undefined);
            const dataLayer = window.dataLayer || [];
            dataLayer.push({
                'event': 'hugo-new-trading-pin-quote-request',
                'userData': {
                    'email': sess?.email || $email.value,
                    'phone': sess?.phone || await p.Format(),
                    'firstName': sess?.firstName || $firstName?.value,
                    'lastName': sess?.lastName || $lastName?.value,
                },
                'transactionId': resp.id,
                'total': resp.quotes?.[0].cachedTotal,
            });

            let nextPage = Relref('/');
            if ($sportSelected.value === 'Softball') {
                nextPage = Relref('/pins/softball-trading-pins');
            } else {
                nextPage = Relref('/pins/baseball-trading-pins');
            }

            new Modal({
                body: `
                <div class="d-flex flex-column align-items-center text-center">
                    <div class="icon-mark icon-mark-lg mb-6">
                        <span class="material-symbols-rounded">task_alt</span>
                    </div>
                    <h2 class="fw-900 mb-2">Proof Request Sent!</h2>
                    <p class="mb-0">
                        You will receive a proof via email in 1-2 business days.
                    </p>
                </div>`,
                confirmText: 'Continue Browsing',
                appendConfirmButtonClass: 'w-100 w-sm-auto btn-sm-lg',
                cancelButton: false,
                appendClassBySelector: {
                    '.modal-dialog': 'proof-request-sent-modal-dialog px-4 px-6',
                    '.modal-body': 'px-6 pt-8 pb-2',
                    '.modal-footer': 'p-6 pt-sm-10 pb-sm-14 justify-content-center',
                },
                onClose: () => {
                    window.location.href = nextPage;
                },
                onSubmit: () => {
                    window.location.href = nextPage;
                },
            });
        }).catch(err => {
            stopLoading();

            HandleRequestError(err, $form);
        });
    });
}
